// src/components/select/SelectTableDialog.tsx
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Stack,
  Typography,
  Box,
} from '@mui/material';
import { CustomDataGrid } from '../CustomDataGrid';
import { GridColDef } from '@mui/x-data-grid-premium';
import { tableSettingsProps } from 'src/hooks/table/tableSettings/useTableSettings';
import { TableSelector } from 'src/hooks/table/tableSettings/useTableSelector';
import { CustomField } from 'src/components/molecules/formik/CustomField';

export type SelectTableDialogProps<Row = any> = TableSelector<Row> & {
  applyButtonText?: string;
};

const SelectTableDialog = <Row,>({
  open,
  onClose,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  openSelector,
  columns,
  tableSettings,
  message,
  clearMessage,
  applySelection,
  clearSelection,
  selectedCount,
  applyButtonText = 'Add selected',
}: SelectTableDialogProps<Row>) => {
  const typedTableSettings = tableSettings as tableSettingsProps;
  const showActions = Boolean(typedTableSettings?.checkboxSelection && applySelection);

  return (
    <>
      <Dialog fullWidth maxWidth="xl" open={open} onClose={onClose}>
        <DialogTitle sx={{ pb: 1.5 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
          >
            <Typography variant="h6">Select items</Typography>

            {/* Quick filter next to the title */}
            <Box sx={{ minWidth: 220, maxWidth: 280 }}>
              <CustomField
                stateProps={{
                  state: typedTableSettings.quickFilterValues,
                  setState: typedTableSettings.setQuickFilterValues,
                }}
                options={{
                  name: 'QuickFilter',
                  label: 'Quick search',
                  height: '40px',
                  topMargin: '0px',
                  className: 'quick-search',
                }}
              />
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          <CustomDataGrid
            columns={columns as GridColDef[]}
            tableSettings={typedTableSettings}
          />
        </DialogContent>

        {showActions && (
          <DialogActions sx={{ px: 2, py: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mr: 'auto', pl: 0.5 }}>
              <Typography variant="body2">
                {selectedCount > 0 ? `${selectedCount} selected` : 'No selection'}
              </Typography>
            </Stack>

            <Button
              onClick={clearSelection}
              disabled={!selectedCount}
              variant="text"
            >
              Clear
            </Button>
            <Button
              onClick={() => applySelection?.({ keepOpen: false })}
              variant="contained"
              disabled={!selectedCount}
            >
              {applyButtonText}
              {selectedCount ? ` (${selectedCount})` : ''}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {message && clearMessage && (
        <Dialog open onClose={clearMessage}>
          <DialogTitle>Category conflict</DialogTitle>
          <DialogContent>
            <DialogContentText>{message}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={clearMessage}>OK</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default SelectTableDialog;
